import { useT } from "../../../shared/i18n/i18n";
import { AtSign, Calendar, ChevronDown, Hash, Link2, ListChecks, MoreHorizontal, Phone, type LucideIcon, Tags, TextCursorInput, ToggleLeft, Users } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { WorkObjectFieldDefinition } from "../../work-object-field/model/use-work-object-fields";
import { isMultiValueField, isRelationField, type WorkObjectFieldValueDraft } from "../model/work-object-field-drafts";
import type { WorkObjectListView } from "../model/use-work-objects";
import { WorkObjectFieldValueEditor } from "./work-object-field-editor";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type WorkObjectSystemFieldValues = {
  title: string;
  typeId: string;
};

type WorkObjectPropertyListProps = {
  currentWorkObjectId?: string;
  fieldDefinitions: WorkObjectFieldDefinition[];
  fieldValueDrafts: Record<string, WorkObjectFieldValueDraft>;
  listView: WorkObjectListView;
  onFieldValueChange: (fieldId: string, value: WorkObjectFieldValueDraft) => void;
  // Убрать поле у ЭТОГО типа карточек. Настройка живёт на экране полей, но
  // нужда в ней возникает здесь: человек видит на баге поле, которому там не
  // место, и чинить это через другой экран он не пойдёт. Действие общее —
  // любое поле, любой тип, — а не выключатель под один случай.
  onHideForType?: (field: WorkObjectFieldDefinition) => void;
  // Names for referenced ids the pickable lists don't cover, so a related item
  // outside the loaded window still reads as itself.
  referenceLabels?: Record<string, string>;
  // Тип карточки: раскрытие пустых полей запоминается на ТИП, а не на человека
  // и не на карточку. Bug у всех выглядит одинаково, и открыв второй баг,
  // человек видит ту же раскладку, что настроил на первом.
  rememberKey?: string | null;
};

// Сколько пустых полей показать, не сворачивая. Прятать одно-два поля за
// раскрывашкой — лишний клик там, где список и так короткий.
const collapseThreshold = 3;

function storageKey(rememberKey: string) {
  return `impacto.fields.expanded.${rememberKey}`;
}

function readExpanded(rememberKey: string | null | undefined): boolean {
  if (!rememberKey) return false;
  try {
    return window.localStorage.getItem(storageKey(rememberKey)) === "true";
  } catch {
    // Приватное окно или запрет на хранение: свёрнуто по умолчанию — это
    // рабочее состояние, а не ошибка.
    return false;
  }
}

// Keep filled fields first, but expose empty fields too: the detail modal is the complete
// object schema, independent of the current board view visibility.
function isEmptyDraft(field: WorkObjectFieldDefinition, draft: WorkObjectFieldValueDraft): boolean {
  if (field.type === "checkbox") return draft !== "true";
  if (Array.isArray(draft)) return draft.length === 0 || draft.every((value) => !value);
  return !draft || (typeof draft === "string" && draft.trim() === "");
}

// Tall controls stack the value below the label; everything else sits inline (label · value).
function isStackedField(field: WorkObjectFieldDefinition): boolean {
  return field.type === "textarea";
}

export function WorkObjectPropertyList({
  currentWorkObjectId,
  fieldDefinitions,
  fieldValueDrafts,
  listView,
  onFieldValueChange,
  onHideForType,
  referenceLabels,
  rememberKey,
}: WorkObjectPropertyListProps) {
  const t = useT();
  const draftFor = (field: WorkObjectFieldDefinition) => fieldValueDrafts[field.id] ?? (isMultiValueField(field.type) ? [] : "");

  // Область заводит поля под все процессы сразу, и на одной карточке их
  // набирается под три десятка. Показанные подряд, они прячут описание и
  // обсуждение под экраном: карточка «раздувается», а всё, что в ней важного,
  // уезжает вниз. Поэтому по умолчанию видно только то, что заполнено или
  // обязательно, а остальное лежит за одной строкой.
  const filledNow = useMemo(() => {
    const ids = new Set<string>();
    for (const field of fieldDefinitions) {
      const empty = isEmptyDraft(field, fieldValueDrafts[field.id] ?? (isMultiValueField(field.type) ? [] : ""));
      // Обязательное поле видно всегда, даже пустое: иначе про него узнают
      // только из отказа при сохранении.
      if (!empty || field.isRequired) ids.add(field.id);
    }
    return ids;
  }, [fieldDefinitions, fieldValueDrafts]);

  // Раскладка замирает, пока открыта одна карточка одного типа.
  //
  // Иначе строка уезжала прямо под курсором: как только в пустом поле
  // появлялся первый символ, поле переходило в группу заполненных и меняло
  // место в списке. Пересчёт только при смене карточки или типа — и ещё один
  // раз, когда с сервера доехали значения (до этого черновики пусты, и
  // замораживать нечего).
  const layoutSignature = `${currentWorkObjectId ?? ""}:${rememberKey ?? ""}`;
  const draftsReady = Object.keys(fieldValueDrafts).length > 0;
  const layout = useRef<{ ready: boolean; signature: string; visibleIds: Set<string> } | null>(null);
  if (!layout.current || layout.current.signature !== layoutSignature || (!layout.current.ready && draftsReady)) {
    layout.current = { ready: draftsReady, signature: layoutSignature, visibleIds: new Set(filledNow) };
  }
  const frozenVisible = layout.current.visibleIds;

  const { alwaysVisible, hidden } = useMemo(() => {
    const shown: WorkObjectFieldDefinition[] = [];
    const rest: WorkObjectFieldDefinition[] = [];
    for (const field of fieldDefinitions) {
      (frozenVisible.has(field.id) ? shown : rest).push(field);
    }
    return { alwaysVisible: shown, hidden: rest };
  }, [fieldDefinitions, frozenVisible]);

  const collapsible = hidden.length > collapseThreshold;
  const [isExpanded, setIsExpanded] = useState(() => readExpanded(rememberKey));
  useEffect(() => setIsExpanded(readExpanded(rememberKey)), [rememberKey]);

  function toggleExpanded() {
    const next = !isExpanded;
    setIsExpanded(next);
    if (!rememberKey) return;
    try {
      window.localStorage.setItem(storageKey(rememberKey), String(next));
    } catch {
      // Раскладка — удобство, а не данные: не сохранилось, и ладно.
    }
  }

  const visibleFields = collapsible && !isExpanded ? alwaysVisible : [...alwaysVisible, ...hidden];

  if (fieldDefinitions.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("board.typeHasNoFields")}</p>;
  }

  return (
    <div className="flex flex-col">
      {visibleFields.length === 0 && !collapsible ? (
        <p className="py-1 text-sm text-muted-foreground">{t("board.allFieldsEmpty")}</p>
      ) : (
        visibleFields.map((field) => (
          <PropertyRow field={field} key={field.id} onHideForType={onHideForType}>
            <WorkObjectFieldValueEditor
              currentWorkObjectId={currentWorkObjectId}
              field={field}
              listView={listView}
              onCommit={(value) => onFieldValueChange(field.id, value)}
              referenceLabels={referenceLabels}
              surface="panel"
              value={draftFor(field)}
            />
          </PropertyRow>
        ))
      )}
      {collapsible ? (
        <button
          className="mt-0.5 flex w-fit items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          onClick={toggleExpanded}
          type="button"
        >
          <ChevronDown className={cn("size-3.5 transition-transform", isExpanded && "rotate-180")} />
          {isExpanded ? t("item.hideEmptyFields") : t("item.moreFields", { count: hidden.length })}
        </button>
      ) : null}
    </div>
  );
}

function PropertyRow({
  children,
  field,
  onHideForType,
}: {
  children: ReactNode;
  field: WorkObjectFieldDefinition;
  onHideForType?: (field: WorkObjectFieldDefinition) => void;
}) {
  const t = useT();
  const Icon = fieldIcon(field);
  const stacked = isStackedField(field);
  // Системные поля — название, тип, статус — карточкой не убираются: без них
  // она перестаёт быть карточкой.
  const canHide = Boolean(onHideForType) && field.origin !== "system";
  return (
    <div className={cn("group/property grid gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/30", stacked ? "grid-cols-1" : "grid-cols-[minmax(7rem,9rem)_minmax(0,1fr)] items-center")}>
      <div className="flex items-center gap-2 pt-px text-muted-foreground">
        <Icon className="size-3.5 shrink-0" />
        <span className="min-w-0 truncate text-xs font-medium">{field.name}</span>
        {canHide ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  aria-label={t("a11y.fieldActions", { name: field.name })}
                  className="ml-auto size-5 shrink-0 opacity-0 transition-opacity group-hover/property:opacity-100 focus-visible:opacity-100 data-[popup-open]:opacity-100"
                  size="icon-sm"
                  variant="ghost"
                >
                  <MoreHorizontal className="size-3" />
                </Button>
              }
            />
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem onClick={() => onHideForType?.(field)}>{t("item.hideFieldForType")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function fieldIcon(field: WorkObjectFieldDefinition): LucideIcon {
  switch (field.type) {
    case "checkbox":
      return ToggleLeft;
    case "currency":
    case "number":
    case "percent":
      return Hash;
    case "date":
    case "date_range":
      return Calendar;
    case "email":
      return AtSign;
    case "multi_select":
      return Tags;
    case "phone":
      return Phone;
    case "select":
      return ListChecks;
    case "textarea":
      return TextCursorInput;
    default:
      if (field.type === "user_relation" || field.type === "user_multi_relation") return Users;
      if (isRelationField(field.type)) return Link2;
      return TextCursorInput;
  }
}
